import { InjectionToken } from '@angular/core';
import { SOCKET_EVENTS } from 'src/app/@core/services/socket.service';

export enum THREAD_AGENT_FEATURES {
    SHARE_THREAD = 'SHARE_THREAD',
    CANVAS = 'CANVAS',
    FEEDBACK = 'FEEDBACK',
    CONNECTORS = 'CONNECTORS',
    OPTIONS = 'OPTIONS',
    USE_STYLES = 'USE_STYLES',
    WEB_SEARCH = 'WEB_SEARCH',
    KNOWLEDGE_BASE = 'KNOWLEDGE_BASE',
    CUSTOM_INSTRUCTIONS = 'CUSTOM_INSTRUCTIONS',
    IMAGE_GENERATION = 'IMAGE_GENERATION',
    PROJECTS = 'PROJECTS',
    LINKED_DOCS = 'LINKED_DOCS',
    COMPARE_DOCS = 'COMPARE_DOCS',
    KPIS = 'KPIS',
    FORM_QUESTIONS = 'FORM_QUESTIONS',
    SIDE_PANEL = 'SIDE_PANEL',
    THREAD_FILES = 'THREAD_FILES',
}

export interface IThreadAgentConfig {
    chatSocketEvent: SOCKET_EVENTS;
    threadDeletedSocketEvent: SOCKET_EVENTS;
    homeRoute: string;
    threadListQueryKey?: readonly unknown[];
    /** Features to exclude. Any feature not listed is considered enabled. */
    excludedFeatures?: readonly THREAD_AGENT_FEATURES[];
}

export function isFeatureEnabled(config: IThreadAgentConfig, feature: THREAD_AGENT_FEATURES): boolean {
    return !(config.excludedFeatures ?? []).includes(feature);
}

export const THREAD_AGENT_CONFIG = new InjectionToken<IThreadAgentConfig>('THREAD_AGENT_CONFIG');

export const GENERAL_AGENT_THREAD_CONFIG: IThreadAgentConfig = {
    chatSocketEvent: SOCKET_EVENTS.GENERAL_AGENT_CHAT,
    threadDeletedSocketEvent: SOCKET_EVENTS.GENERAL_AGENT_THREAD_DELETED,
    homeRoute: '/bob',
    threadListQueryKey: ['threads'],
    excludedFeatures: [THREAD_AGENT_FEATURES.LINKED_DOCS, THREAD_AGENT_FEATURES.COMPARE_DOCS, THREAD_AGENT_FEATURES.KPIS, THREAD_AGENT_FEATURES.FORM_QUESTIONS],
};

export const DOCUMENT_CHAT_THREAD_CONFIG: IThreadAgentConfig = {
    chatSocketEvent: SOCKET_EVENTS.DOCUMENT_CHAT,
    threadDeletedSocketEvent: SOCKET_EVENTS.DOCUMENT_CHAT_THREAD_DELETED,
    homeRoute: '/document-chat',
    threadListQueryKey: undefined,
    excludedFeatures: [
        THREAD_AGENT_FEATURES.SHARE_THREAD,
        THREAD_AGENT_FEATURES.CANVAS,
        THREAD_AGENT_FEATURES.FEEDBACK,
        THREAD_AGENT_FEATURES.CONNECTORS,
        THREAD_AGENT_FEATURES.OPTIONS,
        THREAD_AGENT_FEATURES.USE_STYLES,
        THREAD_AGENT_FEATURES.CUSTOM_INSTRUCTIONS,
        THREAD_AGENT_FEATURES.IMAGE_GENERATION,
        THREAD_AGENT_FEATURES.PROJECTS,
        THREAD_AGENT_FEATURES.COMPARE_DOCS,
        THREAD_AGENT_FEATURES.KPIS,
        THREAD_AGENT_FEATURES.FORM_QUESTIONS,
        THREAD_AGENT_FEATURES.SIDE_PANEL,
    ],
};

export const QNA_CHAT_THREAD_CONFIG: IThreadAgentConfig = {
    chatSocketEvent: SOCKET_EVENTS.QNA_CHAT,
    threadDeletedSocketEvent: SOCKET_EVENTS.QNA_CHAT_THREAD_DELETED,
    homeRoute: '/qna-chat',
    threadListQueryKey: undefined,
    excludedFeatures: [
        THREAD_AGENT_FEATURES.SHARE_THREAD,
        THREAD_AGENT_FEATURES.CANVAS,
        THREAD_AGENT_FEATURES.FEEDBACK,
        THREAD_AGENT_FEATURES.CONNECTORS,
        THREAD_AGENT_FEATURES.OPTIONS,
        THREAD_AGENT_FEATURES.USE_STYLES,
        THREAD_AGENT_FEATURES.CUSTOM_INSTRUCTIONS,
        THREAD_AGENT_FEATURES.IMAGE_GENERATION,
        THREAD_AGENT_FEATURES.PROJECTS,
        THREAD_AGENT_FEATURES.COMPARE_DOCS,
        THREAD_AGENT_FEATURES.KPIS,
        THREAD_AGENT_FEATURES.SIDE_PANEL,
    ],
};

export const COMPARE_CHAT_THREAD_CONFIG: IThreadAgentConfig = {
    chatSocketEvent: SOCKET_EVENTS.COMPARE_CHAT,
    threadDeletedSocketEvent: SOCKET_EVENTS.COMPARE_CHAT_THREAD_DELETED,
    homeRoute: '/compare-chat',
    threadListQueryKey: undefined,
    excludedFeatures: [
        THREAD_AGENT_FEATURES.SHARE_THREAD,
        THREAD_AGENT_FEATURES.CANVAS,
        THREAD_AGENT_FEATURES.FEEDBACK,
        THREAD_AGENT_FEATURES.CONNECTORS,
        THREAD_AGENT_FEATURES.OPTIONS,
        THREAD_AGENT_FEATURES.USE_STYLES,
        THREAD_AGENT_FEATURES.CUSTOM_INSTRUCTIONS,
        THREAD_AGENT_FEATURES.IMAGE_GENERATION,
        THREAD_AGENT_FEATURES.PROJECTS,
        THREAD_AGENT_FEATURES.LINKED_DOCS,
        THREAD_AGENT_FEATURES.FORM_QUESTIONS,
        THREAD_AGENT_FEATURES.SIDE_PANEL,
    ],
};

/**
 * Thread config for geotechnical analysis chat.
 * Uses GEOTECHNICAL_CHAT socket events; documentId carries the analysis report ID.
 * Mirrors DOCUMENT_CHAT_THREAD_CONFIG feature exclusions since this is a focused, document-scoped chat.
 */
export const GEOTECHNICAL_CHAT_THREAD_CONFIG: IThreadAgentConfig = {
    chatSocketEvent: SOCKET_EVENTS.GEOTECHNICAL_CHAT,
    threadDeletedSocketEvent: SOCKET_EVENTS.GEOTECHNICAL_CHAT_THREAD_DELETED,
    homeRoute: '/geotechnical-analysis',
    threadListQueryKey: undefined,
    excludedFeatures: [
        THREAD_AGENT_FEATURES.SHARE_THREAD,
        THREAD_AGENT_FEATURES.CANVAS,
        THREAD_AGENT_FEATURES.FEEDBACK,
        THREAD_AGENT_FEATURES.CONNECTORS,
        THREAD_AGENT_FEATURES.OPTIONS,
        THREAD_AGENT_FEATURES.USE_STYLES,
        THREAD_AGENT_FEATURES.CUSTOM_INSTRUCTIONS,
        THREAD_AGENT_FEATURES.IMAGE_GENERATION,
        THREAD_AGENT_FEATURES.PROJECTS,
        THREAD_AGENT_FEATURES.COMPARE_DOCS,
        THREAD_AGENT_FEATURES.KPIS,
        THREAD_AGENT_FEATURES.FORM_QUESTIONS,
        THREAD_AGENT_FEATURES.SIDE_PANEL,
    ],
};
